import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGoBack } from "@/hooks/use-go-back";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Pencil, Plus, Rocket, Search, Settings } from "lucide-react";
import {
  PRODUCT_TYPES,
  PROFESSIONAL_TYPES,
  Product,
  ProductInput,
  USER_SCOPES,
  createProduct,
  getProducts,
  updateProduct,
} from "@/features/products/products-api";
import { ContentLayout } from "@/components/common/content-layout";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/features/forms/ui/bundles-multi-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { ALL_REGIONS_CODE } from "@/features/regions/constants";
import useBundlesGrouped from "@/features/bundles/hooks/use-bundles-grouped";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import LaunchProductDialog from "@/features/products/ui/launch-product-dialog";
import ParticipantConfigField from "@/features/products/ui/participant-config-field";
import DashboardOrderField from "@/features/products/ui/dashboard-order-field";

const EMPTY: ProductInput = {
  name: "",
  description: "",
  unit_price: 0,
  category: "",
  sub_category: "",
  type: "TELE_HEALTH",
  user_scope: "SINGLE_USER",
  status: "ACTIVE",
  notify_user_template_type: "",
  notify_professional_template_type: "",
  professional_type: [],
  available_session_minutes: [],
  participant_config: undefined,
};

export default function Products() {
  const navigate = useNavigate();
  const goBack = useGoBack("../bundles");
  const { region = ALL_REGIONS_CODE } = useParams();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [launching, setLaunching] = useState<Product | null>(null);
  const [form, setForm] = useState<ProductInput>(EMPTY);
  const [minutesText, setMinutesText] = useState("");
  const [pcValid, setPcValid] = useState(true);

  const { data: regions } = useActiveRegions();
  const regionId = regions?.find((r) => r.code === region)?.id;

  const { data: products = [], isLoading, isFetching, error } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });

  // A product is "launched" in the current region if it has a bundle there.
  // The grouped-bundles endpoint is already region-filtered, so its product
  // ids are exactly the launched ones. Only meaningful for a specific region.
  const specificRegion = region !== ALL_REGIONS_CODE;
  const { data: grouped } = useBundlesGrouped({});
  const launchedProductIds = useMemo(
    () =>
      new Set(
        (grouped?.groups ?? [])
          .map((g) => g.product?.id)
          .filter(Boolean) as string[],
      ),
    [grouped],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return products;
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(s) ||
        (p.category ?? "").toLowerCase().includes(s) ||
        (p.sub_category ?? "").toLowerCase().includes(s) ||
        (p.type ?? "").toLowerCase().includes(s),
    );
  }, [products, q]);

  const save = useMutation({
    mutationFn: () => {
      const payload: ProductInput = {
        ...form,
        available_session_minutes: minutesText
          .split(",")
          .map((m) => parseInt(m.trim(), 10))
          .filter((n) => !Number.isNaN(n)),
      };
      return editing ? updateProduct(editing.id, payload) : createProduct(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] });
      setOpen(false);
      toast({ title: editing ? "Product updated" : "Product created" });
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setMinutesText("");
    setPcValid(true);
    setOpen(true);
  };
  const openEdit = (p: Product) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description ?? "",
      unit_price: p.unit_price,
      category: p.category,
      sub_category: p.sub_category ?? "",
      type: p.type,
      user_scope: p.user_scope,
      status: p.status,
      notify_user_template_type: p.notify_user_template_type,
      notify_professional_template_type: p.notify_professional_template_type,
      professional_type: p.professional_type ?? [],
      available_session_minutes: p.available_session_minutes ?? [],
      participant_config: p.participant_config ?? undefined,
    });
    setMinutesText((p.available_session_minutes ?? []).join(", "));
    setPcValid(true);
    setOpen(true);
  };

  const regionLabel = region === ALL_REGIONS_CODE ? "" : ` in ${region.toUpperCase()}`;

  return (
    <>
      <ContentLayout
        header={
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={goBack}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-xl font-semibold">
                Products{" "}
                <span className="text-sm font-normal text-gray-400">
                  (region-independent)
                </span>
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-64 max-w-full">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, category, type…"
                  className="h-8 pl-7 text-sm"
                />
              </div>
              {/* Lives in the header rather than floating over the table, so
                  it never sits on top of a row's action buttons. */}
              {specificRegion && (
                <DashboardOrderField
                  regionId={regionId}
                  regionCode={region}
                  products={products}
                  launchedProductIds={launchedProductIds}
                />
              )}
              <HasAccess permission="write">
                <Button className="bg-sidebar" onClick={openCreate}>
                  <Plus className="h-4 w-4 mr-1" /> New Product
                </Button>
              </HasAccess>
            </div>
          </div>
        }
        data={filtered}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage="Failed to load products"
        emptyState={{ message: q ? "No products match." : "No products yet" }}
      >
        <div className="border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500">
                <th className="py-2 px-4">Name</th>
                <th className="py-2 px-4">Category</th>
                <th className="py-2 px-4">Type</th>
                <th className="py-2 px-4">User scope</th>
                <th className="py-2 px-4">Unit price</th>
                <th className="py-2 px-4">Session minutes</th>
                <th className="py-2 px-4">Status</th>
                <th className="py-2 px-4 w-44" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-gray-100">
                  <td className="py-2 px-4">
                    <div className="font-medium">{p.name}</div>
                    {p.sub_category && (
                      <div className="text-[11px] text-gray-400">
                        {p.sub_category}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-4">
                    <Badge variant="outline">{p.category}</Badge>
                  </td>
                  <td className="py-2 px-4">{p.type}</td>
                  <td className="py-2 px-4">{p.user_scope}</td>
                  <td className="py-2 px-4">{p.unit_price}</td>
                  <td className="py-2 px-4">
                    {p.available_session_minutes?.length
                      ? p.available_session_minutes.map((m) => `${m}m`).join(", ")
                      : "—"}
                  </td>
                  <td className="py-2 px-4">
                    <Badge variant={p.status === "ACTIVE" ? "active" : "inactive"}>
                      {p.status}
                    </Badge>
                  </td>
                  <td className="py-2 px-4">
                    <div className="flex items-center gap-1 justify-end">
                      <HasAccess permission="write">
                        {specificRegion && launchedProductIds.has(p.id) ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled
                            className="text-green-600"
                            title={`Already launched${regionLabel}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Launched
                            {regionLabel}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            title={`Create a bundle for this product${regionLabel}`}
                            onClick={() =>
                              specificRegion && regionId
                                ? setLaunching(p)
                                : navigate(`../bundles/new?product_id=${p.id}`)
                            }
                          >
                            <Rocket className="h-3.5 w-3.5 mr-1" /> Launch
                            {regionLabel}
                          </Button>
                        )}
                        {specificRegion && (
                          <Button
                            size="icon"
                            variant="ghost"
                            title={`Configure onboarding${regionLabel}`}
                            onClick={() => navigate(`${p.id}/config`)}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(p)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </HasAccess>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentLayout>

      <LaunchProductDialog
        product={launching}
        regionCode={region}
        regionId={regionId}
        regionLabel={regionLabel}
        onClose={() => setLaunching(null)}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Product" : "New Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Name</p>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Product name"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Description</p>
              <Input
                value={form.description ?? ""}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Category</p>
                <Input
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                  placeholder="e.g. MENTAL_HEALTH"
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Sub-category</p>
                <Input
                  value={form.sub_category ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, sub_category: e.target.value })
                  }
                  placeholder="Sub-category"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Type</p>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm({ ...form, type: v as any })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCT_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">User scope</p>
                <Select
                  value={form.user_scope}
                  onValueChange={(v) =>
                    setForm({ ...form, user_scope: v as any })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="User scope" />
                  </SelectTrigger>
                  <SelectContent>
                    {USER_SCOPES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Unit price</p>
                <Input
                  type="number"
                  value={form.unit_price}
                  onChange={(e) =>
                    setForm({ ...form, unit_price: Number(e.target.value) || 0 })
                  }
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Status</p>
                <Select
                  value={form.status}
                  onValueChange={(v) =>
                    setForm({ ...form, status: v as "ACTIVE" | "INACTIVE" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Professional types</p>
              <MultiSelect
                options={PROFESSIONAL_TYPES.map((t) => ({ label: t, value: t }))}
                value={form.professional_type ?? []}
                onChange={(v) => setForm({ ...form, professional_type: v })}
                placeholder="Select professional types"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">
                Available session minutes{" "}
                <span className="text-gray-400">(comma-separated)</span>
              </p>
              <Input
                value={minutesText}
                onChange={(e) => setMinutesText(e.target.value)}
                placeholder="e.g. 30, 45, 60"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Notify user template
                </p>
                <Input
                  value={form.notify_user_template_type}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      notify_user_template_type: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Notify professional template
                </p>
                <Input
                  value={form.notify_professional_template_type}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      notify_professional_template_type: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <ParticipantConfigField
              value={form.participant_config}
              onChange={(pc) => setForm({ ...form, participant_config: pc })}
              onValidityChange={setPcValid}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => save.mutate()}
                disabled={
                  save.isPending || !form.name || !form.category || !pcValid
                }
              >
                {save.isPending ? "Saving…" : editing ? "Save changes" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
